import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import { ChevronLeft, ChevronRight, ArrowLeft, CalendarDays, Plus, Trash2, X, Video, Pencil } from 'lucide-react';
import GridMes, { TagDia } from './GridMes';
import useCalendarioMes from './useCalendarioMes';
import FormEvento from './FormEvento';
import PainelFeriados from './PainelFeriados';
import { CAMADA, MESES, PERFIS_GESTAO_FERIADO, authHeaders, modalOverlay, modalCard, modalHeader, btnX } from './calendario-ui';

// =====================================================================
// CALENDÁRIO (Home)
// =====================================================================
// Tela cheia, acessível pelo botão "Ver calendário completo" do card da Home.
// Mostra, num grid de calendário (uma célula por dia), com navegação entre meses:
//   - ANIVERSARIANTES do mês  (/api/home/aniversariantes?mes=N) — tags vermelhas.
//     Recorte normal por empresa/loja; Admin/Marketing veem só PARTNER.
//   - FERIADOS cadastrados     (/api/calendario/feriados) — tags amarelas, rede toda.
//     Admin/Marketing podem GERIR (cadastrar/remover) via painel próprio.
//   - EVENTOS                  (/api/calendario/eventos) — tags verdes, com
//     compartilhamento por perfil/loja. Qualquer logado cria; o criador remove.
// Aniversário é recorrente (só dia/mês); o ANO exibido é o corrente, usado para
// alinhar os dias da semana e para casar os feriados (que são datas específicas).
//
// ⚠️ Esta tela e o modal do ícone de calendário (BotaoAgenda -> CalendarioAgenda)
// são as DUAS caras do mesmo calendário — o modal ainda soma os compromissos do
// Teams. Para não divergirem, tudo que é comum mora fora: GridMes (o desenho),
// useCalendarioMes (as buscas), calendario-ui (as cores das camadas) e os
// formulários FormEvento/PainelFeriados. Mexeu numa cara, olhe a outra.

const COR = {
    paginaFundo: '#f2f2f2',
    branco: '#ffffff',
    vermelhoEscuro: '#990605',
    texto: '#1a1a1a',
    bordaSuave: 'rgba(199,194,194,0.45)',
};
const FONTE = "'Raleway', sans-serif";
const SOMBRA = '0 4px 8px rgba(0,0,0,0.12)';
// (DIAS_SEMANA saiu daqui: o cabeçalho da semana agora é do GridMes. As cores das
// camadas e MESES/PERFIS_GESTAO_FERIADO saíram para calendario-ui — o modal usa
// os mesmos.)

function CalendarioAniversariantes() {
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    const hoje = new Date();
    // Ano e mês são estado JUNTOS: feriado e evento são datas específicas, então
    // passar de dezembro precisa virar o ano (antes o ano era fixo e dezembro
    // levava a janeiro do MESMO ano, mostrando feriados/eventos do passado).
    const [ano, setAno] = useState(hoje.getFullYear());
    const [mes, setMes] = useState(hoje.getMonth() + 1);   // 1..12
    const [gestaoAberta, setGestaoAberta] = useState(false);
    const [eventoFormAberto, setEventoFormAberto] = useState(false);
    const [eventoDetalhe, setEventoDetalhe] = useState(null);
    const [eventoEditar, setEventoEditar] = useState(null);   // evento em edição (abre o FormEvento)
    // Evento criado no portal cujo espelho no Teams falhou. Precisa de aviso
    // VISÍVEL: a pessoa marcou "criar no Teams" e isso não aconteceu — sem dizer,
    // ela contaria com um convite que não existe.
    const [avisoTeams, setAvisoTeams] = useState('');

    // Quem pode cadastrar feriados (lê o perfil do usuário logado).
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const podeGerir = PERFIS_GESTAO_FERIADO.includes(user?.perfil);

    useEffect(() => {
        const onResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', onResize);
        onResize();
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // As três camadas (aniversariantes, feriados, eventos) + o agrupamento por dia
    // vêm prontos do hook — compartilhado com o modal do ícone de calendário.
    const {
        aniversariantes, feriados, opcoes, carregando,
        anivPorDia, feriadosPorDia, eventosPorDia,
        recarregarFeriados, recarregarEventos,
    } = useCalendarioMes(ano, mes);

    // Navegação pelo Date: ele mesmo resolve a virada de ano (mês -1 e 12 são
    // datas válidas). Mesmo `irMes()` do CalendarioAgenda (modal do cabeçalho).
    const irMes = (passo) => {
        const d = new Date(ano, mes - 1 + passo, 1);
        setAno(d.getFullYear());
        setMes(d.getMonth() + 1);
    };

    const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));

    // Fundo da célula: só o dia com aniversariante ganha um rosa bem claro. O
    // feriado NÃO pinta mais (era azul): agora que a tag dele é amarela, um fundo
    // creme brigaria com a borda amarela do "hoje" — viraria a mesma pista visual.
    const destaqueDia = (dia) => (anivPorDia[dia]?.length ? '#fffafa' : null);

    // Tags do dia, na ordem: feriado > evento > aniversariante. As cores vêm do
    // CAMADA compartilhado — o modal do cabeçalho pinta exatamente igual.
    const renderDia = (dia) => (
        <>
            {(feriadosPorDia[dia] || []).map((f, j) => (
                <TagDia
                    key={`f${j}`} {...CAMADA.feriado} texto={f.nome}
                    title={`Feriado ${f.tipo}${f.municipio ? ' · ' + f.municipio : ''}${f.uf ? '/' + f.uf : ''}: ${f.nome}`}
                />
            ))}
            {(eventosPorDia[dia] || []).map((ev) => (
                <TagDia
                    key={`e${ev.id}`} {...CAMADA.evento} texto={ev.titulo}
                    title={ev.titulo} onClick={() => setEventoDetalhe(ev)}
                />
            ))}
            {(anivPorDia[dia] || []).map((p, j) => (
                <TagDia
                    key={`a${j}`} {...CAMADA.aniversario}
                    texto={(p.nome || '').split(' ')[0]}
                    title={`${p.nome} · ${p.cargo} · ${p.loja}`}
                    icone={
                        <span style={{ width: '18px', height: '18px', flexShrink: 0, borderRadius: '50%', backgroundColor: COR.vermelhoEscuro, color: '#fff', fontSize: '9px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {(p.nome || '?').charAt(0).toUpperCase()}
                        </span>
                    }
                />
            ))}
        </>
    );

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: COR.paginaFundo, fontFamily: FONTE }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />

            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease', padding: '28px 32px', minWidth: 0 }}>

                {/* Voltar */}
                <button
                    onClick={() => navigate('/home')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelhoEscuro, fontFamily: FONTE, fontWeight: 600, fontSize: '14px', padding: 0, marginBottom: '14px' }}
                >
                    <ArrowLeft size={18} /> Voltar para a Home
                </button>

                {/* Cabeçalho: título + (gerir feriados) + navegação de mês */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CalendarDays size={28} color={COR.vermelhoEscuro} />
                        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: COR.texto }}>Calendário</h1>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        {/* Criar evento é de escritório/gerente (2026-07-16). Quem
                            responde é o backend (opcoes.podeCriar) — não uma cópia
                            da regra aqui. A trava real é o 403 da rota. */}
                        {opcoes.podeCriar && (
                            <button
                                onClick={() => setEventoFormAberto(true)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: CAMADA.evento.cor, color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontFamily: FONTE, fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                            >
                                <Plus size={16} /> Adicionar evento
                            </button>
                        )}
                        {podeGerir && (
                            <button
                                onClick={() => setGestaoAberta(true)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: CAMADA.feriado.cor, color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontFamily: FONTE, fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                            >
                                <Plus size={16} /> Gerenciar feriados
                            </button>
                        )}
                        <span style={{ fontSize: '13px', color: '#777' }}>
                            {carregando ? 'carregando…' : `${aniversariantes.length} aniversariante${aniversariantes.length === 1 ? '' : 's'} neste mês`}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: COR.branco, borderRadius: '10px', boxShadow: SOMBRA, padding: '6px 8px' }}>
                            <button onClick={() => irMes(-1)} title="Mês anterior" style={botaoNav}><ChevronLeft size={20} /></button>
                            <span style={{ minWidth: '170px', textAlign: 'center', fontWeight: 700, fontSize: '15px', color: COR.texto }}>{MESES[mes - 1]} {ano}</span>
                            <button onClick={() => irMes(1)} title="Próximo mês" style={botaoNav}><ChevronRight size={20} /></button>
                        </div>
                    </div>
                </div>

                {/* Aviso do espelho no Teams (só aparece quando o Teams falhou) */}
                {avisoTeams && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' }}>
                        <span>{avisoTeams}</span>
                        <button onClick={() => setAvisoTeams('')} style={{ ...btnX, color: '#92400e' }} title="Fechar aviso"><X size={16} /></button>
                    </div>
                )}

                {/* Legenda — as 3 camadas desta tela (o Teams só existe no modal do
                    cabeçalho, então fica de fora aqui). Mesmas cores do CAMADA. */}
                <div style={{ display: 'flex', gap: '18px', marginBottom: '14px', fontSize: '12px', color: '#777' }}>
                    {['aniversario', 'feriado', 'evento'].map((chave) => (
                        <span key={chave} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: CAMADA[chave].fundo, border: `1px solid ${CAMADA[chave].cor}` }} />
                            {CAMADA[chave].rotulo}
                        </span>
                    ))}
                </div>

                {/* Grid do calendário — desenho compartilhado com o modal da agenda */}
                <div style={{ backgroundColor: COR.branco, borderRadius: '10px', boxShadow: SOMBRA, padding: '18px' }}>
                    <GridMes ano={ano} mes={mes} renderDia={renderDia} destaqueDia={destaqueDia} />
                </div>
            </div>

            {/* Painel de gestão de feriados (Admin/Marketing) — compartilhado com o modal */}
            {gestaoAberta && (
                <PainelFeriados
                    ano={ano}
                    mes={mes}
                    feriados={feriados}
                    onFechar={() => setGestaoAberta(false)}
                    onMudou={recarregarFeriados}
                />
            )}

            {/* Adicionar evento (escritório/gerente) — compartilhado com o modal */}
            {eventoFormAberto && (
                <FormEvento
                    ano={ano}
                    mes={mes}
                    opcoes={opcoes}
                    onFechar={() => setEventoFormAberto(false)}
                    onCriou={(aviso) => { setEventoFormAberto(false); setAvisoTeams(aviso || ''); recarregarEventos(); }}
                />
            )}

            {/* Editar evento (só o criador) — reusa o FormEvento em modo edição */}
            {eventoEditar && (
                <FormEvento
                    ano={ano}
                    mes={mes}
                    opcoes={opcoes}
                    evento={eventoEditar}
                    onFechar={() => setEventoEditar(null)}
                    onCriou={(aviso) => { setEventoEditar(null); setAvisoTeams(aviso || ''); recarregarEventos(); }}
                />
            )}

            {/* Detalhe do evento (ver + editar/excluir se for meu) */}
            {eventoDetalhe && (
                <EventoDetalhe
                    evento={eventoDetalhe}
                    onFechar={() => setEventoDetalhe(null)}
                    onEditar={() => { const ev = eventoDetalhe; setEventoDetalhe(null); setEventoEditar(ev); }}
                    onRemoveu={() => { setEventoDetalhe(null); recarregarEventos(); }}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Detalhe do evento: vê os dados + remove (se for o criador).
// ---------------------------------------------------------------------
// Continua local a esta tela: só aqui a tag verde é clicável. No modal do
// cabeçalho o evento é informativo (a gestão é feita nesta tela). Se um dia o
// modal precisar do detalhe, este componente vira arquivo próprio — como já
// aconteceu com o FormEvento e o PainelFeriados.
function EventoDetalhe({ evento, onFechar, onEditar, onRemoveu }) {
    const [removendo, setRemovendo] = useState(false);

    const remover = async () => {
        setRemovendo(true);
        try {
            const r = await fetch(`/api/calendario/eventos/${evento.id}`, { method: 'DELETE', headers: authHeaders() });
            if (r.ok) onRemoveu();
        } catch (e) { /* ignora */ } finally { setRemovendo(false); }
    };

    const fmt = (s) => (s || '').split('-').reverse().join('/');
    const ehPeriodo = evento.dataFim && evento.dataFim !== evento.dataInicio;
    const quandoLabel = ehPeriodo ? `${fmt(evento.dataInicio)} a ${fmt(evento.dataFim)}` : fmt(evento.dataInicio);
    const alvos = evento.compartilhamentos || [];
    // `rotulo` vem pronto do backend — para o alvo do tipo 'usuario' ele já é o
    // NOME da pessoa (o VALOR guardado é o ID, que não diz nada na tela).
    const NOME_TIPO = { perfil: 'Perfil', loja: 'Loja', usuario: 'Pessoa' };
    const compartLabel = alvos.length === 0
        ? 'Privado (só o criador)'
        : alvos.map((a) => `${NOME_TIPO[a.tipo] || a.tipo}: ${a.rotulo || a.valor}`).join(' · ');

    return (
        <div onClick={onFechar} style={modalOverlay}>
            <div onClick={(e) => e.stopPropagation()} style={{ ...modalCard, width: '420px' }}>
                <div style={modalHeader}>
                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: COR.texto }}>{evento.titulo}</h3>
                    <button onClick={onFechar} style={btnX}><X size={20} /></button>
                </div>
                <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '14px', color: COR.texto }}>
                    <div><strong>{ehPeriodo ? 'Período' : 'Data'}:</strong> {quandoLabel}</div>
                    {evento.descricao && <div style={{ whiteSpace: 'pre-wrap', color: '#444' }}>{evento.descricao}</div>}
                    {/* Só existe quando o evento foi espelhado no Teams gerando reunião */}
                    {evento.linkOnline && (
                        <a
                            href={evento.linkOnline} target="_blank" rel="noopener noreferrer"
                            style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: CAMADA.teams.cor, color: '#fff', borderRadius: '8px', padding: '8px 14px', fontWeight: 700, fontSize: '13px', textDecoration: 'none' }}
                        >
                            <Video size={15} /> Entrar na reunião do Teams
                        </a>
                    )}
                    <div style={{ color: '#777', fontSize: '13px' }}><strong>Criado por:</strong> {evento.criador || '—'}</div>
                    <div style={{ color: '#777', fontSize: '13px' }}><strong>Compartilhado:</strong> {compartLabel}</div>
                    {evento.meu && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                            <button onClick={onEditar} disabled={removendo} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: CAMADA.evento.cor, color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontFamily: FONTE, fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                                <Pencil size={15} /> Editar
                            </button>
                            <button onClick={remover} disabled={removendo} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 14px', fontFamily: FONTE, fontWeight: 700, fontSize: '13px', cursor: removendo ? 'default' : 'pointer' }}>
                                <Trash2 size={16} /> {removendo ? 'Removendo…' : 'Excluir'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const botaoNav = {
    background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelhoEscuro,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px',
};

export default CalendarioAniversariantes;
